/**
 * usePickerSelection — the workshop picker's interaction state: which workshop
 * is highlighted, which day's drawer is open, and the Escape behaviour that ties
 * the two together (BON-146).
 *
 * Split out of {@link WorkshopPicker} so that component is chrome and nothing
 * else. The picker owns no data; this is the only state it does own.
 */
import { useEffect, useState } from 'react';

import type { PickerWorkshop } from './pickerWorkshop';
import { workshopsOnDay } from './WorkshopPickerHelpers';

export function usePickerSelection(
  workshops: PickerWorkshop[],
  onBack: () => void,
  onPick: (workshop: PickerWorkshop) => void,
) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dayDrawer, setDayDrawer] = useState<Date | null>(null);

  // Escape closes the day drawer if it's open; otherwise returns to whatever
  // opened the picker (this is a nested browse layer, not the whole flow) —
  // mirrors the header/footer "Back" affordance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || dayDrawer) return;
      onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack, dayDrawer]);

  // Recomputed at open time from the CURRENT list — NOT just the events the cell
  // hid — so the drawer lists every workshop that day, cap-fitting ones too.
  const drawerWorkshops = dayDrawer ? workshopsOnDay(workshops, dayDrawer) : [];

  return {
    selectedId,
    setSelectedId,
    selected: workshops.find((w) => w.id === selectedId) ?? null,
    dayDrawer,
    openDayDrawer: setDayDrawer,
    closeDayDrawer: () => setDayDrawer(null),
    drawerWorkshops,
    /** A drawer row is a DIRECT pick: it closes and commits, unlike the
     *  calendar's select-then-confirm footer flow. */
    pickFromDrawer: (id: string) => {
      const workshop = drawerWorkshops.find((w) => w.id === id);
      setDayDrawer(null);
      if (workshop) onPick(workshop);
    },
  };
}
